import React, { useEffect, useRef, useState } from 'react';
import { StylishNewButton } from './StylishNewButton';
import classNames from 'classnames';

type PratusInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'value' | 'defaultValue' | 'onChange'
> & {
  label?: string;
  onValueChange?: (value: string) => void;
  onSave?: (value: string) => void;
  onCancel?: () => void;
  defaultValue?: string;
  value?: string;
  showErrorContainer?: boolean;
};

export default function PratusInput({
  minLength = 0,
  value,
  defaultValue,
  onCancel,
  onSave,
  onValueChange,
  showErrorContainer = true,
  maxLength,
  ...props
}: PratusInputProps) {
  const isControlled = value !== undefined;
  const initialValue = isControlled ? (value ?? '') : (defaultValue ?? '');

  const [inputValue, setInputValue] = useState(initialValue);
  const [lastSavedValue, setLastSavedValue] = useState(initialValue);

  // Sync internal state when external value/defaultValue changes.
  // If there are no unsaved edits (inputValue === lastSavedValue), update both.
  // If the user has unsaved edits, preserve the draft but refresh lastSavedValue.
  useEffect(() => {
    const next = value !== undefined ? (value ?? '') : (defaultValue ?? '');
    setLastSavedValue(next);
    if (inputValue === lastSavedValue) {
      setInputValue(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, defaultValue]);

  const inputRef = useRef<HTMLInputElement>(null);

  const handleSave: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.currentTarget.blur();
    inputRef.current?.blur();

    onValueChange?.(inputValue);
    setLastSavedValue(inputValue);
    onSave?.(inputValue);
  };

  const handleCancel: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.currentTarget.blur();
    inputRef.current?.blur();
    setInputValue(lastSavedValue);
    onCancel?.();
  };

  const isError = inputValue.length < minLength;

  return (
    <div className={classNames('pratus-input-field mb-1', props.className)}>
      {props.label && (
        <label className="form-label" htmlFor={props.id}>
          {props.label}
        </label>
      )}
      <Input
        {...props}
        ref={inputRef}
        className="mb-2 form-control"
        value={inputValue}
        maxLength={maxLength}
        onChange={(e) => setInputValue(e.target.value)}
      />
      {maxLength !== undefined && (
        <div className="form-text text-muted mb-1 pratus-input-char-count">
          {inputValue.length} / {maxLength}
        </div>
      )}

      <div className="d-flex flex-column justify-content-between">
        <div className="btn-container d-flex flex-row">
          <StylishNewButton
            className="me-2 button--sml"
            onClick={handleSave}
            disabled={isError}
          >
            Save
          </StylishNewButton>
          <StylishNewButton
            className="button--secondary button--sml"
            onClick={handleCancel}
          >
            Cancel
          </StylishNewButton>
        </div>
        {showErrorContainer && (
          <div
            className="form-text no-icon text-danger fs-6"
            style={{
              visibility:
                inputValue.length > 0 && isError ? 'visible' : 'hidden',
            }}
          >
            Enter at least {minLength} characters
          </div>
        )}
      </div>
    </div>
  );
}

type InputProps = React.InputHTMLAttributes<HTMLInputElement>;

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ style, ...props }, ref) => {
    return (
      <input
        ref={ref}
        {...props}
        style={{ borderRadius: '6px', paddingLeft: '9px', ...style }}
      />
    );
  }
);
Input.displayName = 'BaseInput';
